/**
 * Generic modal that presents a single-choice radio group backed by the
 * oasis-ui-kit `RadioButton`. Option list, title, description, and default
 * selection are read from `state.modal.metaData`; save / dismiss callbacks
 * are supplied by the parent as props so they are not stored in Redux.
 */
import { Modal, RadioButton } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ModalId } from "../../../enums/modalId";
import { RootState } from "../../../redux/store/store";

const DEFAULT_TITLE = "Select an Option";

const RADIO_GROUP_NAME = "radio-select-modal-group";

/** One selectable option rendered inside the radio group. */
export interface RadioSelectOption {
  /** Human-readable label shown next to the control. */
  label: string;
  /** Stable value submitted when this option is chosen. */
  value: string;
}

/**
 * Data the modal reads from `state.modal.metaData` when
 * `ModalId.RADIO_SELECT_MODAL` is open.
 */
export interface RadioSelectModalMetaData {
  /** Identifier of the order the selection applies to. */
  order_id: string;
  /** Options shown in the radio group. */
  options: RadioSelectOption[];
  /** Value string of the option selected by default, if any. */
  preselectedValue?: string;
  /** Modal title text. */
  title?: string;
  /** Line of copy rendered above the radio group. */
  description?: string;
  isLoading?: boolean;
}

/** Callback props for {@link RadioSelectModal}; not stored in Redux. */
interface RadioSelectModalProps {
  /**
   * Invoked when the user confirms with a valid selection; receives the
   * full option object matching the current `value` and the `order_id` the
   * selection applies to.
   */
  onSave: (selected: RadioSelectOption, orderId: string) => void;
  /**
   * Invoked when the dialog is dismissed via the close affordance (X / overlay).
   */
  onClose: () => void;
  /** Invoked when the user clicks the cancel button. */
  onCancel: () => void;
}

/**
 * Renders a modal whose title, optional description, and radio options come
 * from Redux modal metadata, while lifecycle callbacks are passed in from the
 * parent.
 *
 * @param props.onSave - Called with the selected `{ label, value }` and the
 *   `order_id` on Save.
 * @param props.onClose - Called when the modal requests a full dismiss.
 * @param props.onCancel - Called when the user cancels.
 */
const RadioSelectModal = ({
  onSave,
  onClose,
  onCancel,
}: RadioSelectModalProps) => {
  const metaData = useSelector((state: RootState) => state.modal.metaData) as
    | RadioSelectModalMetaData
    | undefined;

  const options = useMemo(() => metaData?.options ?? [], [metaData?.options]);

  const [selectedValue, setSelectedValue] = useState<string>("");

  useEffect(() => {
    setSelectedValue(metaData?.preselectedValue ?? "");
  }, [metaData?.preselectedValue]);

  const modalTitle = metaData?.title?.trim() ? metaData.title : DEFAULT_TITLE;

  const handleSave = useCallback(() => {
    const selected = options.find((opt) => opt.value === selectedValue);
    if (selected) {
      onSave(selected, metaData?.order_id ?? "");
    }
  }, [onSave, options, selectedValue, metaData?.order_id]);

  const hasValidSelection =
    Boolean(selectedValue.trim()) &&
    options.some((opt) => opt.value === selectedValue);

  const isSaveDisabled = !hasValidSelection || options.length === 0;

  return (
    <Modal
      dialogId={ModalId.RADIO_SELECT_MODAL}
      title={modalTitle}
      onSave={handleSave}
      onClose={onClose}
      onCancel={onCancel}
      saveButtonText={metaData?.isLoading ? "Saving..." : "Save"}
      cancelText="Cancel"
      disableSave={isSaveDisabled || metaData?.isLoading}
      maxHeightPercentage={40}
    >
      <div className="radio-select-modal flex flex-col gap-3">
        {metaData?.description ? (
          <div className="radio-select-modal__description text-body font-normal text-primaryGray-2">
            {metaData.description}
          </div>
        ) : null}
        <div className="radio-select-modal__radios">
          <RadioButton
            name={RADIO_GROUP_NAME}
            direction="vertical"
            value={selectedValue}
            onChange={setSelectedValue}
            options={options}
          />
        </div>
      </div>
    </Modal>
  );
};

export default RadioSelectModal;
